import { useState, useEffect } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  EyeOff,
  Clock,
  MapPin,
  User,
  Image as ImageIcon,
  MessageSquare,
  Shield,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  updateDoc,
  doc,
  serverTimestamp,
  type Timestamp,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { Avatar } from './ui/Avatar';
import { ConfirmationModal } from './ui/ConfirmationModal';
import { Tooltip } from './ui/Tooltip';
import { ReportCategory } from '@/types/reports';
import { moderationTooltips, getCategoryApproveTooltip, getCategoryRejectTooltip } from '@/lib/admin-tooltips';

interface ReportData {
  id: string;
  politician?: string;
  location: string;
  description?: string;
  imageUrl?: string;
  authorName?: string;
  authorPhoto?: string | null;
  createdAt?: Timestamp | Date;
  userId?: string;
  status?: 'pending' | 'approved' | 'rejected';
  isPublic?: boolean;
  rejectionReason?: string;
  reviewedBy?: string;
  reviewedAt?: Timestamp | Date;
  reporter?: {
    userId: string;
    displayName?: string;
    photoUrl?: string | null;
  };
  // Phase 2: Report category
  reportCategory?: ReportCategory;
  // DILG Anti-Epal Drive fields (for ANTI_EPAL category)
  isGovernmentFunded?: boolean;
  projectType?: 'government_project' | 'government_program' | 'government_activity' | 'government_property';
  projectName?: string;
  fundingSource?: 'national' | 'local' | 'both';
  violationType?: 'name' | 'image' | 'likeness' | 'combination';
  // Additional fields for other categories
  expectedTimeline?: string;
  serviceType?: string;
  complianceType?: string;
  geo?: {
    lat: number;
    lng: number;
    accuracy?: number;
  } | null;
  imageMeta?: {
    width: number;
    height: number;
    sizeBytes: number;
    mimeType: string;
  } | null;
  communitySignals?: {
    validVotes: number;
    invalidVotes: number;
    abuseFlags: number;
    commentsCount: number;
  };
}

interface AdminReportManagementProps {
  currentUserId: string;
  currentUserRole: 'root' | 'admin'; // Reserved for future role-based permissions
  onClose?: () => void; // Optional for page mode
}

type FilterType = 'all' | 'pending' | 'approved' | 'rejected';
type CommunityFilterType = 
  | 'all' 
  | 'high-upvotes' 
  | 'high-downvotes' 
  | 'reported' 
  | 'needs-attention' 
  | 'popular' 
  | 'no-engagement';

type SortType = 
  | 'newest' 
  | 'oldest' 
  | 'most-upvoted' 
  | 'most-downvoted' 
  | 'most-reported' 
  | 'most-commented' 
  | 'highest-engagement';

export const AdminReportManagement = ({
  currentUserId,
  currentUserRole, // Reserved for future role-based permissions
  onClose,
}: AdminReportManagementProps) => {
  // Suppress unused variable warning for future use
  void currentUserRole;
  const [allReports, setAllReports] = useState<ReportData[]>([]); // All fetched reports
  const [filter, setFilter] = useState<FilterType>('all');
  const [communityFilter, setCommunityFilter] = useState<CommunityFilterType>('all');
  const [categoryFilter, setCategoryFilter] = useState<ReportCategory | 'all'>('all');
  const [sortBy, setSortBy] = useState<SortType>('newest');
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  
  // DILG-specific rejection reasons
  const dilgRejectionReasons = [
    'Not a government-funded project',
    'Insufficient evidence',
    'False or misleading information',
    'Personal attack or defamation',
    'Not related to Anti-Epal policy violation',
    'Duplicate report',
    'Violation not clearly visible in photo',
    'Project is privately funded',
    'Other (specify)'
  ];
  const [confirmation, setConfirmation] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Fetch all reports with pagination
  useEffect(() => {
    let isMounted = true;
    setIsLoadingReports(true);

    const fetchReports = async () => {
      try {
        // Fetch reports in batches (we'll fetch a large batch for filtering/sorting)
        // In a production app, you might want to fetch all or use a reasonable limit
        const reportsCollection = collection(db, 'artifacts', appId, 'public', 'data', 'reports');
        const q = query(reportsCollection, orderBy('createdAt', 'desc'), limit(1000));
        const snapshot = await getDocs(q);
        
        if (!isMounted) return;
        
        const fetchedReports = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as ReportData[];
        
        setAllReports(fetchedReports);
      } catch (error) {
        console.error('[AdminReportManagement] Error fetching reports:', error);
        alert('Failed to load reports. Please refresh the page.');
      } finally {
        if (isMounted) {
          setIsLoadingReports(false);
        }
      }
    };

    fetchReports();

    return () => {
      isMounted = false;
    };
  }, []);

  // Helper function to convert Timestamp | Date to Date
  const toDate = (value: Timestamp | Date | undefined): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if ('toDate' in value) return value.toDate();
    return null;
  };

  // Reset to page 1 when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, communityFilter, categoryFilter, sortBy]);

  const filteredReports = allReports.filter((report) => {
    // Phase 2: Category filter
    const reportCat = report.reportCategory || 'ANTI_EPAL';
    const categoryMatch = categoryFilter === 'all' || reportCat === categoryFilter;

    // Status filter (existing)
    const status = report.status || 'pending';
    let statusMatch = true;
    switch (filter) {
      case 'pending':
        statusMatch = status === 'pending';
        break;
      case 'approved':
        statusMatch = status === 'approved';
        break;
      case 'rejected':
        statusMatch = status === 'rejected';
        break;
      default:
        statusMatch = true;
    }

    // Community signal filter (new)
    const signals = report.communitySignals || {
      validVotes: 0,
      invalidVotes: 0,
      abuseFlags: 0,
      commentsCount: 0,
    };

    let communityMatch = true;
    switch (communityFilter) {
      case 'high-upvotes':
        communityMatch = signals.validVotes >= 10;
        break;
      case 'high-downvotes':
        communityMatch = signals.invalidVotes >= 5;
        break;
      case 'reported':
        communityMatch = signals.abuseFlags > 0;
        break;
      case 'needs-attention':
        communityMatch = signals.invalidVotes >= 3 || signals.abuseFlags >= 2;
        break;
      case 'popular':
        communityMatch = signals.validVotes >= 5 && signals.commentsCount >= 3;
        break;
      case 'no-engagement':
        communityMatch = 
          signals.validVotes === 0 && 
          signals.invalidVotes === 0 && 
          signals.abuseFlags === 0 && 
          signals.commentsCount === 0;
        break;
      default:
        communityMatch = true;
    }

    return categoryMatch && statusMatch && communityMatch;
  });

  // Sort filtered reports
  const sortedReports = [...filteredReports].sort((a, b) => {
    const aSignals = a.communitySignals || {
      validVotes: 0,
      invalidVotes: 0,
      abuseFlags: 0,
      commentsCount: 0,
    };
    const bSignals = b.communitySignals || {
      validVotes: 0,
      invalidVotes: 0,
      abuseFlags: 0,
      commentsCount: 0,
    };

    const aDate = toDate(a.createdAt) || new Date(0);
    const bDate = toDate(b.createdAt) || new Date(0);

    switch (sortBy) {
      case 'newest':
        return bDate.getTime() - aDate.getTime();
      case 'oldest':
        return aDate.getTime() - bDate.getTime();
      case 'most-upvoted':
        return bSignals.validVotes - aSignals.validVotes;
      case 'most-downvoted':
        return bSignals.invalidVotes - aSignals.invalidVotes;
      case 'most-reported':
        return bSignals.abuseFlags - aSignals.abuseFlags;
      case 'most-commented':
        return bSignals.commentsCount - aSignals.commentsCount;
      case 'highest-engagement': {
        const aEngagement = aSignals.validVotes + aSignals.commentsCount - aSignals.invalidVotes - aSignals.abuseFlags;
        const bEngagement = bSignals.validVotes + bSignals.commentsCount - bSignals.invalidVotes - bSignals.abuseFlags;
        return bEngagement - aEngagement;
      }
      default:
        return bDate.getTime() - aDate.getTime();
    }
  });

  // Pagination calculations
  const totalReports = sortedReports.length;
  const totalPages = Math.ceil(totalReports / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedReports = sortedReports.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of reports list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const handleApprove = async (report: ReportData) => {
    setLoading(true);
    try {
      const reportRef = doc(db, 'artifacts', appId, 'public', 'data', 'reports', report.id);
      await updateDoc(reportRef, {
        status: 'approved',
        reviewedBy: currentUserId,
        reviewedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('[AdminReportManagement] Error approving report:', error);
      alert('Failed to approve report');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (report: ReportData) => {
    setLoading(true);
    try {
      const reportRef = doc(db, 'artifacts', appId, 'public', 'data', 'reports', report.id);
      await updateDoc(reportRef, {
        isPublic: true,
        reviewedBy: currentUserId,
        reviewedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('[AdminReportManagement] Error publishing report:', error);
      alert('Failed to publish report');
    } finally {
      setLoading(false);
    }
  };

  const handleHide = async (report: ReportData) => {
    setLoading(true);
    try {
      const reportRef = doc(db, 'artifacts', appId, 'public', 'data', 'reports', report.id);
      await updateDoc(reportRef, {
        isPublic: false,
        reviewedBy: currentUserId,
        reviewedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('[AdminReportManagement] Error hiding report:', error);
      alert('Failed to hide report');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedReport) return;
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    try {
      const reportRef = doc(
        db,
        'artifacts',
        appId,
        'public',
        'data',
        'reports',
        selectedReport.id
      );
      await updateDoc(reportRef, {
        status: 'rejected',
        isPublic: false,
        rejectionReason: rejectionReason.trim(),
        reviewedBy: currentUserId,
        reviewedAt: serverTimestamp(),
      });
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedReport(null);
    } catch (error) {
      console.error('[AdminReportManagement] Error rejecting report:', error);
      alert('Failed to reject report');
    } finally {
      setLoading(false);
    }
  };

  const openRejectModal = (report: ReportData) => {
    setSelectedReport(report);
    setRejectionReason(report.rejectionReason || '');
    setShowRejectModal(true);
  };

  const getStatusBadge = (status?: string) => {
    const reportStatus = status || 'pending';
    let badge;
    let tooltipContent;
    
    switch (reportStatus) {
      case 'pending':
        badge = (
          <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
            <Clock size={12} />
            Pending
          </span>
        );
        tooltipContent = moderationTooltips.status.pending;
        break;
      case 'approved':
        badge = (
          <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded">
            <CheckCircle size={12} />
            Approved
          </span>
        );
        tooltipContent = moderationTooltips.status.approved;
        break;
      case 'rejected':
        badge = (
          <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded">
            <XCircle size={12} />
            Rejected
          </span>
        );
        tooltipContent = moderationTooltips.status.rejected;
        break;
      default:
        return null;
    }
    
    return (
      <Tooltip content={tooltipContent} position="top">
        {badge}
      </Tooltip>
    );
  };

  const isModalMode = !!onClose;

  const pageContent = (
    <>
      {/* Filters */}
      <div className={`${isModalMode ? 'p-4 border-b border-gray-200 shrink-0' : 'mb-6'} flex gap-2 flex-wrap`}>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({allReports.length})
          </button>
          <Tooltip content={moderationTooltips.status.pending} position="bottom">
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending ({allReports.filter((r) => (r.status || 'pending') === 'pending').length})
            </button>
          </Tooltip>
          <Tooltip content={moderationTooltips.status.approved} position="bottom">
            <button
              onClick={() => setFilter('approved')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'approved'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Approved ({allReports.filter((r) => r.status === 'approved').length})
            </button>
          </Tooltip>
          <Tooltip content={moderationTooltips.status.rejected} position="bottom">
            <button
              onClick={() => setFilter('rejected')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'rejected'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rejected ({allReports.filter((r) => r.status === 'rejected').length})
            </button>
          </Tooltip>
        </div>

      {/* Phase 2: Category Filter */}
      <div className={`${isModalMode ? 'p-4 border-b border-gray-200 shrink-0' : 'mb-4'}`}>
        <Tooltip
          content="Each category has specific approval/rejection criteria. Review category-specific rules before moderating."
          position="top"
          showIcon
        >
          <label htmlFor="admin-report-category-filter" className="block text-sm font-semibold text-gray-700 mb-2">
            Filter by Category
          </label>
        </Tooltip>
        <select
          id="admin-report-category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as ReportCategory | 'all')}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
        >
          <option value="all">All Categories</option>
          <option value="ANTI_EPAL">Anti-Epal Watch</option>
          <option value="PROJECT_DELAY">Project Delay</option>
          <option value="GHOST_PROJECT">Ghost Project</option>
          <option value="SERVICE_FAILURE">Service Failure</option>
          <option value="COMPLIANCE_GAP">Compliance Gap</option>
        </select>
      </div>

      {/* Community Signal Filters & Sort */}
      <div className={`${isModalMode ? 'p-4 border-b border-gray-200 shrink-0' : 'mb-4'} flex items-center gap-3 flex-wrap`}>
        {/* Community Filter Dropdown */}
        <div className="relative">
          <select
            value={communityFilter}
            onChange={(e) => setCommunityFilter(e.target.value as CommunityFilterType)}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
          >
            <option value="all">All Community Signals</option>
            <option value="high-upvotes">High Upvotes (≥10)</option>
            <option value="high-downvotes">High Downvotes (≥5)</option>
            <option value="reported">Reported/Flagged</option>
            <option value="needs-attention">Needs Attention</option>
            <option value="popular">Popular</option>
            <option value="no-engagement">No Engagement</option>
          </select>
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortType)}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="most-upvoted">Most Upvoted</option>
            <option value="most-downvoted">Most Downvoted</option>
            <option value="most-reported">Most Reported</option>
            <option value="most-commented">Most Commented</option>
            <option value="highest-engagement">Highest Engagement</option>
          </select>
        </div>

        {/* Page Size Selector */}
        <div className="relative">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing page size
            }}
            className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
          >
            <option value="20">20 per page</option>
            <option value="30">30 per page</option>
            <option value="50">50 per page</option>
          </select>
        </div>

        {/* Results Count */}
        <div className="text-sm text-gray-600 ml-auto">
          {isLoadingReports ? (
            <span>Loading reports...</span>
          ) : (
            <span>
              Showing {startIndex + 1}-{Math.min(endIndex, totalReports)} of {totalReports} reports
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </span>
          )}
        </div>
      </div>

      {/* Reports List */}
      <div className={`${isModalMode ? 'flex-1 overflow-y-auto p-4' : ''}`}>
          {isLoadingReports ? (
            <div className="text-center py-12 text-gray-500">
              <Clock size={48} className="mx-auto mb-4 text-gray-400 animate-spin" />
              <p>Loading reports...</p>
            </div>
          ) : paginatedReports.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle size={48} className="mx-auto mb-4 text-gray-400" />
              <p>No reports found for this filter.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedReports.map((report) => (
                <div
                  key={report.id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors"
                >
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-200 shrink-0">
                      {report.imageUrl ? (
                        <img
                          src={report.imageUrl}
                          alt="Report"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <ImageIcon size={24} />
                        </div>
                      )}
                    </div>

                    {/* Report Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 truncate">
                            {report.politician}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                            <MapPin size={14} />
                            <span className="truncate">{report.location}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {getStatusBadge(report.status)}
                          {report.isPublic === false && (
                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded">
                              <EyeOff size={12} />
                              Hidden
                            </span>
                          )}
                        </div>
                      </div>

                      {report.description && (
                        <p className="text-sm text-gray-700 mb-2 line-clamp-2">
                          {report.description}
                        </p>
                      )}

                      {/* Metadata Row */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                        <div className="flex items-center gap-1">
                          <User size={12} />
                          <span>
                            {report.reporter?.displayName ||
                              report.authorName ||
                              'Anonymous'}
                          </span>
                        </div>
                        <span>•</span>
                        <span>
                          {toDate(report.createdAt)?.toLocaleDateString() || 'Unknown date'}
                        </span>
                        {report.communitySignals && (
                          <>
                            <span>•</span>
                            <div className="flex items-center gap-3">
                              <Tooltip content={moderationTooltips.communitySignals.votes} position="top">
                                <span className="text-green-600 cursor-help">
                                  ✓ {report.communitySignals.validVotes || 0}
                                </span>
                              </Tooltip>
                              <Tooltip content={moderationTooltips.communitySignals.highDownvotes} position="top">
                                <span className="text-orange-600 cursor-help">
                                  ✗ {report.communitySignals.invalidVotes || 0}
                                </span>
                              </Tooltip>
                              <Tooltip content={moderationTooltips.communitySignals.flags} position="top">
                                <span className="text-red-600 cursor-help">
                                  🚩 {report.communitySignals.abuseFlags || 0}
                                </span>
                              </Tooltip>
                              <Tooltip content={moderationTooltips.comments.rule} position="top">
                                <span className="text-blue-600 cursor-help">
                                  <MessageSquare size={12} className="inline" />{' '}
                                  {report.communitySignals.commentsCount || 0}
                                </span>
                              </Tooltip>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={() => setSelectedReport(report)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          View Details
                        </button>
                        {report.status !== 'approved' && (
                          <Tooltip
                            content={report.reportCategory ? getCategoryApproveTooltip(report.reportCategory) : moderationTooltips.actions.approve}
                            position="top"
                          >
                            <button
                              onClick={() => handleApprove(report)}
                              disabled={loading}
                              className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              Approve
                            </button>
                          </Tooltip>
                        )}
                        {report.status === 'approved' && (
                          <>
                            {report.isPublic ? (
                              <Tooltip content={moderationTooltips.actions.hide} position="top">
                                <button
                                  onClick={() => handleHide(report)}
                                  disabled={loading}
                                  className="text-sm bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 disabled:opacity-50"
                                >
                                  Hide
                                </button>
                              </Tooltip>
                            ) : (
                              <button
                                onClick={() => handlePublish(report)}
                                disabled={loading}
                                className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                Publish
                              </button>
                            )}
                          </>
                        )}
                        {report.status !== 'rejected' && (
                          <Tooltip
                            content={report.reportCategory ? getCategoryRejectTooltip(report.reportCategory) : moderationTooltips.actions.reject}
                            position="top"
                          >
                            <button
                              onClick={() => openRejectModal(report)}
                              disabled={loading}
                              className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Pagination Controls */}
      {!isLoadingReports && totalPages > 1 && (
        <div className={`${isModalMode ? 'p-4 border-t border-gray-200 shrink-0' : 'mt-6'} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  // Show all pages if 5 or fewer
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  // Show first 5 pages
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // Show last 5 pages
                  pageNum = totalPages - 4 + i;
                } else {
                  // Show pages around current page
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium min-w-[40px] ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
          
          {/* Page Info */}
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}

      {/* Report Detail Modal */}
      {selectedReport && !showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Report Details</h3>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Image */}
              {selectedReport.imageUrl && (
                <div className="rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={selectedReport.imageUrl}
                    alt="Report evidence"
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Basic Info */}
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Politician</h4>
                <p className="text-gray-700">{selectedReport.politician}</p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-2">Location</h4>
                <p className="text-gray-700">{selectedReport.location}</p>
              </div>

              {selectedReport.description && (
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Description</h4>
                  <p className="text-gray-700">{selectedReport.description}</p>
                </div>
              )}

              {/* Reporter Info */}
              <div>
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <User size={18} />
                  Reporter
                </h4>
                <div className="flex items-center gap-3">
                  <Avatar
                    name={
                      selectedReport.reporter?.displayName ||
                      selectedReport.authorName ||
                      'Anonymous'
                    }
                    url={selectedReport.reporter?.photoUrl || selectedReport.authorPhoto}
                    size="md"
                  />
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedReport.reporter?.displayName ||
                        selectedReport.authorName ||
                        'Anonymous'}
                    </p>
                    <p className="text-sm text-gray-500">
                      User ID: {selectedReport.reporter?.userId || selectedReport.userId || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Geolocation */}
              {selectedReport.geo && (
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <MapPin size={18} />
                    Geolocation
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Latitude:</strong> {selectedReport.geo.lat.toFixed(6)}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Longitude:</strong> {selectedReport.geo.lng.toFixed(6)}
                    </p>
                    {selectedReport.geo.accuracy && (
                      <p className="text-sm text-gray-700">
                        <strong>Accuracy:</strong> ±{selectedReport.geo.accuracy.toFixed(0)}m
                      </p>
                    )}
                    <a
                      href={`https://www.google.com/maps?q=${selectedReport.geo.lat},${selectedReport.geo.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm mt-2 inline-block"
                    >
                      View on Google Maps →
                    </a>
                  </div>
                </div>
              )}

              {/* Image Metadata */}
              {selectedReport.imageMeta && (
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <ImageIcon size={18} />
                    Image Metadata
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                    <p className="text-sm text-gray-700">
                      <strong>Dimensions:</strong> {selectedReport.imageMeta.width} ×{' '}
                      {selectedReport.imageMeta.height}px
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>File Size:</strong>{' '}
                      {(selectedReport.imageMeta.sizeBytes / 1024).toFixed(2)} KB
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>MIME Type:</strong> {selectedReport.imageMeta.mimeType}
                    </p>
                  </div>
                </div>
              )}

              {/* Community Signals */}
              {selectedReport.communitySignals && (
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Shield size={18} />
                    Community Validation
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Upvotes:</span>
                      <span className="font-semibold text-green-600">
                        {selectedReport.communitySignals.validVotes || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Downvotes:</span>
                      <span className="font-semibold text-orange-600">
                        {selectedReport.communitySignals.invalidVotes || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Reports:</span>
                      <span className="font-semibold text-red-600">
                        {selectedReport.communitySignals.abuseFlags || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Comments:</span>
                      <span className="font-semibold text-blue-600">
                        {selectedReport.communitySignals.commentsCount || 0}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200 mt-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Engagement Score:</span>
                        <span className="font-semibold text-gray-900">
                          {(selectedReport.communitySignals.validVotes || 0) + 
                           (selectedReport.communitySignals.commentsCount || 0) - 
                           (selectedReport.communitySignals.invalidVotes || 0) - 
                           (selectedReport.communitySignals.abuseFlags || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Moderation Info */}
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Moderation Status</h4>
                <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                  <p className="text-sm text-gray-700">
                    <strong>Status:</strong> {getStatusBadge(selectedReport.status)}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Public:</strong>{' '}
                    {selectedReport.isPublic ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-gray-600">No</span>
                    )}
                  </p>
                  {selectedReport.reviewedBy && (
                    <p className="text-sm text-gray-700">
                      <strong>Reviewed By:</strong> {selectedReport.reviewedBy}
                    </p>
                  )}
                  {selectedReport.reviewedAt && (
                    <p className="text-sm text-gray-700">
                      <strong>Reviewed At:</strong>{' '}
                      {toDate(selectedReport.reviewedAt)?.toLocaleString() || 'N/A'}
                    </p>
                  )}
                  {selectedReport.rejectionReason && (
                    <div>
                      <p className="text-sm text-gray-700 font-medium mb-1">
                        Rejection Reason:
                      </p>
                      <p className="text-sm text-gray-600 bg-red-50 p-2 rounded">
                        {selectedReport.rejectionReason}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                {selectedReport.status !== 'approved' && (
                  <Tooltip
                    content={selectedReport.reportCategory ? getCategoryApproveTooltip(selectedReport.reportCategory) : moderationTooltips.actions.approve}
                    position="top"
                    maxWidth="280px"
                  >
                    <button
                      onClick={() => {
                        handleApprove(selectedReport);
                        setSelectedReport(null);
                      }}
                      disabled={loading}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                  </Tooltip>
                )}
                {selectedReport.status === 'approved' && (
                  <>
                    {selectedReport.isPublic ? (
                      <Tooltip content={moderationTooltips.actions.hide} position="top" maxWidth="280px">
                        <button
                          onClick={() => {
                            handleHide(selectedReport);
                            setSelectedReport(null);
                          }}
                          disabled={loading}
                          className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 disabled:opacity-50"
                        >
                          Hide from Public
                        </button>
                      </Tooltip>
                    ) : (
                      <button
                        onClick={() => {
                          handlePublish(selectedReport);
                          setSelectedReport(null);
                        }}
                        disabled={loading}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Publish Publicly
                      </button>
                    )}
                  </>
                )}
                {selectedReport.status !== 'rejected' && (
                  <Tooltip
                    content={selectedReport.reportCategory ? getCategoryRejectTooltip(selectedReport.reportCategory) : moderationTooltips.actions.reject}
                    position="top"
                    maxWidth="280px"
                  >
                    <button
                      onClick={() => openRejectModal(selectedReport)}
                      disabled={loading}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Reject Report - DILG Anti-Epal Policy</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for rejecting this report. This helps maintain
                transparency and ensures compliance with DILG Memorandum NC-2026-1047.
              </p>
              
              {/* Quick selection buttons */}
              <div className="space-y-2 mb-4">
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Common Rejection Reasons (DILG Policy):
                </label>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {dilgRejectionReasons.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setRejectionReason(reason === 'Other (specify)' ? '' : reason)}
                      className={`text-left px-3 py-2 text-sm rounded-lg border transition-colors ${
                        rejectionReason === reason || (reason === 'Other (specify)' && rejectionReason && !dilgRejectionReasons.slice(0, -1).includes(rejectionReason))
                          ? 'bg-red-50 border-red-300 text-red-700'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Custom reason textarea */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Rejection Reason:
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none text-sm"
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Select a reason above or enter custom reason..."
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                    setSelectedReport(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={loading || !rejectionReason.trim()}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Rejecting...' : 'Reject Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        onConfirm={confirmation.onConfirm}
        onClose={() => setConfirmation((prev) => ({ ...prev, isOpen: false }))}
      />
    </>
  );

  // If modal mode, wrap in modal container
  if (isModalMode) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
            <div className="flex items-center gap-2">
              <Shield className="text-blue-600" size={24} />
              <h2 className="text-2xl font-bold text-gray-900">Citizen Report Moderation</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          {pageContent}
        </div>
      </div>
    );
  }

  // Otherwise, return as page component
  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="text-blue-600" size={28} />
          <h1 className="text-3xl font-bold text-gray-900">Citizen Report Moderation</h1>
        </div>
        <p className="text-gray-600">Manage and moderate all citizen reports submitted by users</p>
      </div>
      {pageContent}
    </div>
  );
};
